<template>
  <v-container >
      <!-- SALUDO AL USUARIO -->
  <!-- SALUDO CON ICONO -->
    <v-row justify="center" class="">
      <v-col cols="12" md="8" class="text-center">
        <div class="d-flex align-center justify-center text-h6">
          <v-icon large class="mr-2">mdi-account-circle</v-icon>
          Buenas tardes, <strong class="ml-1">{{ $store.state.auth.user.username }}</strong>
        </div>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <h1 class="display-1 font-weight-bold mb-6">Panel de Administración</h1>
      </v-col>
    </v-row>

    <!-- KPIs -->
    <v-row dense>
      <v-col cols="12" md="3" v-for="card in kpiCards" :key="card.title">
        <v-hover v-slot="{ hover }">
          <v-card
            :class="['pa-4 elevation-3 rounded-xl', hover ? 'elevation-12' : '']"
            :color="card.color"
            dark
            style="cursor: pointer"
            @click="$router.push(card.link)"
          >
            <div class="d-flex justify-space-between align-center">
              <div>
                <h3 class="subtitle-1 font-weight-light mb-1">{{ card.title }}</h3>
                <div class="text-h5 font-weight-bold">{{ card.value }}</div>
              </div>
              <v-icon size="36">{{ card.icon }}</v-icon>
            </div>
            <div class="text-caption mt-2">{{ card.description }}</div>
          </v-card>
        </v-hover>
      </v-col>
    </v-row>

    <!-- Últimas empresas -->
    <v-row>
      <v-col cols="12" md="6">
        <v-card class="rounded-xl elevation-2">
          <v-card-title class="headline font-weight-bold">Últimas empresas registradas</v-card-title>
          <v-divider></v-divider>
          <v-list>
            <v-list-item
              v-for="empresa in empresasRecientes"
              :key="empresa.id"
              @click="$router.push(`/admin/empresa/${empresa.uuid}`)"
              class="hoverable"
            >
              <v-list-item-content>
                <v-list-item-title class="font-weight-medium">{{ empresa.nombre }}</v-list-item-title>
                <v-list-item-subtitle>{{ empresa.fecha }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon>mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>

      <!-- Actividad reciente -->
      <v-col cols="12" md="6">
        <v-card class="rounded-xl elevation-2">
          <v-card-title class="headline font-weight-bold">Actividad del sistema</v-card-title>
          <v-divider></v-divider>
          <v-list>
            <v-list-item v-for="actividad in actividadSistema" :key="actividad.id">
              <v-list-item-content>
                <v-list-item-title>{{ actividad.evento }}</v-list-item-title>
                <v-list-item-subtitle>{{ actividad.fecha }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-icon>
                <v-icon color="grey">mdi-clock-time-four-outline</v-icon>
              </v-list-item-icon>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
    </v-row>

    <!-- Novedades de la versión -->
    <v-row>
      <v-col cols="12">
        <v-card class="rounded-xl elevation-2">
          <v-card-title class="headline font-weight-bold">
            <v-icon left>mdi-bell-outline</v-icon>
            Novedades
            <v-spacer />
            <v-chip small outlined color="primary">{{ version }}</v-chip>
          </v-card-title>
          <v-card-subtitle v-if="release">
            {{ release.titulo }} · {{ formatFecha(release.fecha) }}
          </v-card-subtitle>
          <v-divider></v-divider>
          <v-list two-line>
            <v-list-item v-for="(novedad, i) in novedades" :key="i">
              <v-list-item-icon class="mr-4">
                <v-chip x-small outlined :color="colorTag(novedad.tag)">
                  {{ novedad.tag }}
                </v-chip>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle class="text-wrap">
                  {{ novedad.texto }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
    </v-row>

    <!-- Certificados AFIP -->
    <v-row>
      <v-col cols="12">
        <v-card class="rounded-xl elevation-2">
          <v-card-title class="headline font-weight-bold">Certificados AFIP por Sucursal</v-card-title>
          <v-divider></v-divider>
          <v-data-table
            :headers="certHeaders"
            :items="certificadosAfip"
            class="elevation-0"
            dense
          >
            <template v-slot:item.estado="{ item }">
              <v-chip
                :color="item.estado === 'vigente' ? 'green' : 'red'"
                dark
                small
              >
                {{ item.estado }}
              </v-chip>
            </template>
            <template v-slot:item.accion="{ item }">
              <v-btn
                icon
                :to="`/admin/empresas/${item.uuid_empresa}/sucursales/${item.uuid_sucursal}/certificados`"
              >
                <v-icon>mdi-open-in-new</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { RELEASES, novedadesRecientes } from '@/changelog';
import { mapGetters } from 'vuex';

export default {
  name: 'AdminDashboard',
  computed: {
    // La versión es la que está corriendo: la sirve el backend, no el front.
    ...mapGetters('empresa', ['empresa_version']),
    version() {
      return this.empresa_version || '—';
    },
    release() {
      return RELEASES[0];
    },
    novedades() {
      return novedadesRecientes('admin');
    },
  },
  methods: {
    formatFecha(fecha) {
      if (!fecha) return '';
      const [y, m, d] = fecha.split('-');
      return `${d}/${m}/${y}`;
    },
    colorTag(tag) {
      return {
        'CORRECCIÓN': 'red lighten-1',
        'DATOS': 'amber darken-2',
        'RENDIMIENTO': 'teal lighten-1',
      }[tag] || 'blue-grey lighten-1';
    },
  },
  data() {
    return {
      kpiCards: [
        {
          title: 'Empresas',
          value: '124',
          icon: 'mdi-domain',
          description: 'Registradas en total',
          color: 'indigo',
          link: '/admin/empresas'
        },
        {
          title: 'Usuarios activos',
          value: '342',
          icon: 'mdi-account-multiple',
          description: 'Últimos 30 días',
          color: 'teal',
          link: '/admin/usuarios'
        },
        {
          title: 'Comprobantes',
          value: '9,843',
          icon: 'mdi-file-document-multiple',
          description: 'Emitidos este mes',
          color: 'deep-purple',
          link: '/admin/comprobantes'
        },
        {
          title: 'Configuraciones',
          value: 'Config',
          icon: 'mdi-cog-outline',
          description: 'Módulos y soporte',
          color: 'grey darken-1',
          link: '/admin/soporte'
        },
        {
          title: 'Suscripciones',
          value: 'Planes',
          icon: 'mdi-package-variant',
          description: 'Gestión de planes y accesos',
          color: 'blue darken-2',
          link: '/admin/suscripciones'
        },
      ],
      empresasRecientes: [
        { id: 1, nombre: 'La Genovesa S.A.', fecha: '01/06/2025', uuid: 'uuid123' },
        { id: 2, nombre: 'Frutas El Alba', fecha: '02/06/2025', uuid: 'uuid456' },
        { id: 3, nombre: 'Distribuidora Del Sol', fecha: '03/06/2025', uuid: 'uuid789' },
      ],
      actividadSistema: [
        { id: 1, evento: 'Nuevo usuario registrado', fecha: 'Hoy 08:45' },
        { id: 2, evento: 'Factura emitida', fecha: 'Hoy 09:15' },
        { id: 3, evento: 'Actualización de plan de cuentas', fecha: 'Ayer 16:30' },
      ],
      certificadosAfip: [
        {
          id: 1,
          empresa: 'La Genovesa S.A.',
          sucursal: 'Sucursal Central',
          estado: 'vigente',
          uuid_empresa: 'uuid123',
          uuid_sucursal: 'suc001'
        },
        {
          id: 2,
          empresa: 'Frutas El Alba',
          sucursal: 'Tucumán',
          estado: 'vencido',
          uuid_empresa: 'uuid456',
          uuid_sucursal: 'suc002'
        },
        {
          id: 3,
          empresa: 'Distribuidora Del Sol',
          sucursal: 'San Juan',
          estado: 'vigente',
          uuid_empresa: 'uuid789',
          uuid_sucursal: 'suc003'
        }
      ],
      certHeaders: [
        { text: 'Empresa', value: 'empresa' },
        { text: 'Sucursal', value: 'sucursal' },
        { text: 'Estado Certificado', value: 'estado' },
        { text: 'Acción', value: 'accion', sortable: false },
      ],
    };
  },
};
</script>

<style scoped>
.display-1 {
  font-size: 2.8rem;
}
.rounded-xl {
  border-radius: 20px;
}
.hoverable {
  transition: background 0.2s ease;
}
.hoverable:hover {
  background-color: #f5f5f5;
}
</style>
